import { useEffect, useMemo, useState } from 'react'

import { ReactNativeLDClient } from '@launchdarkly/react-native-client-sdk'
import { LDClient } from 'launchdarkly-react-client-sdk'

import { useSearchAgeAndType } from './useSearchAgeAndType'
import { useShouldShowForkedTriageEntrypoint } from './useShouldShowForkedTriageEntrypoint'
import { CustomerPropertiesMap, GetTriageMetadataActions, TriageScreener } from '../../../models'
import { COMMON_MIXPANEL_PAGE } from '../../common/constants/mixpanelConstants'
import { SumoLogLevel } from '../../common/utils/commonUtils'
import { useSendMultiplePrimaryNeedsExperimentDataForHomebase } from '../../experiments/hooks/useSendMultiplePrimaryNeedsExperimentDataForHomebase'
import { ExperimentPayload, ExperimentUUIDs } from '../../experiments/types'
import { INDIVIDUAL_COACHING_TOOLKIT } from '../../wellnessToolkit/constants'
import {
  useToolkitAvailabilityMap,
  useToolkitAvailabilityMapParams,
} from '../../wellnessToolkit/hooks/useToolkitAvailabilityMap'
import { WELLNESS_TOOLKIT_PROMOTION } from '../../wellnessToolkit/types'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '../constants'
import { getAge } from '../utils'

export interface useWorkForceMentalHealthHomebasePropsArgs extends useToolkitAvailabilityMapParams {
  getTriageMetadata: (arg: GetTriageMetadataActions) => Promise<any>
  customerPropertiesMap?: CustomerPropertiesMap
  triageScreenersMetadataVersion?: string
  shouldFetchTriageMetadata?: boolean
  deviceUUID?: string
  postExperimentEntrypoint?: (payload: ExperimentPayload) => void
  ldClient?: LDClient | ReactNativeLDClient
  isHeaderExperimentEnabled?: boolean
  canSelectMultiplePrimaryNeeds?: boolean
  searchId?: string | null
  customerName?: string
  route?: string
  logToSumo?: (data: { message: string; type: SumoLogLevel }) => void
  isAiInTriageEnabledThreeArm: string
  isAiInTriageEnabledThreeArmInExperiment: boolean
}

/** For shared WFMH homebase code between LW and native. Currently gets forked triage entrypoint availability, primary
 *  needs triage metadata and toolkit availability for family hub, parent coaching and individual coaching */
export const useWorkForceMentalHealthHomebaseProps = ({
  lyraId,
  isUserTeen,
  triageSearches,
  isCoachingEnabled,
  isTherapyEnabled,
  isCoachingForParentsEnabled,
  riskFactors,
  customerPropertiesMap,
  episodes,
  isUserLoggedIn,
  isInternationalUser,
  postSpeculativeSearch,
  userDOB,
  userCountryName,
  isEssentialsEnabled,
  hideDependentField,
  hideChildSearchCTA,
  isFamilyHubEnabled,
  isCoachingEntryPointEnabled,
  isCoachingForParentsEntryPointEnabled,
  displayLanguage,
  isSpeculativeFilterEnabled,
  speculativeSearchThresholds,
  route,
  logToSumo,
  getTriageMetadata,
  triageScreenersMetadataVersion,
  shouldFetchTriageMetadata = false,
  isLyraSelect,
  deviceUUID,
  postExperimentEntrypoint,
  ldClient,
  isHeaderExperimentEnabled,
  canSelectMultiplePrimaryNeeds,
  searchId,
  customerName,
  isAiInTriageEnabledThreeArm,
  isAiInTriageEnabledThreeArmInExperiment,
}: useWorkForceMentalHealthHomebasePropsArgs) => {
  const [isLoadingTriageMetadata, setIsLoadingTriageMetadata] = useState(shouldFetchTriageMetadata)
  const [primaryNeedsMetadata, setPrimaryNeedsMetadata] = useState<TriageScreener>()
  const getMetadataAge = useSearchAgeAndType()

  const context = useMemo(() => {
    return {
      page: COMMON_MIXPANEL_PAGE.HOME_BASE,
      lyraId,
      isUserTeen,
      triageSearches,
      isCoachingEnabled,
      isTherapyEnabled,
      isCoachingForParentsEnabled,
      riskFactors,
      episodes,
      isUserLoggedIn,
      isInternationalUser,
      postSpeculativeSearch,
      userDOB,
      userCountryName,
      isEssentialsEnabled,
      hideDependentField,
      hideChildSearchCTA,
      isFamilyHubEnabled,
      isCoachingEntryPointEnabled,
      isCoachingForParentsEntryPointEnabled,
      displayLanguage,
      isSpeculativeFilterEnabled,
      speculativeSearchThresholds,
      route,
      logToSumo,
      isLyraSelect,
    }
  }, [
    lyraId,
    isUserTeen,
    triageSearches,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    riskFactors,
    episodes,
    isUserLoggedIn,
    isInternationalUser,
    postSpeculativeSearch,
    userDOB,
    userCountryName,
    isEssentialsEnabled,
    hideDependentField,
    hideChildSearchCTA,
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isCoachingForParentsEntryPointEnabled,
    displayLanguage,
    isSpeculativeFilterEnabled,
    speculativeSearchThresholds,
    route,
    logToSumo,
    isLyraSelect,
  ])

  const { availabilityByToolkit, isLoading } = useToolkitAvailabilityMap(context)

  const { isToolkitAvailable: isIndividualCoachingAvailable, reasons: reasonsForIndividualCoachingAvailabilityStatus } =
    availabilityByToolkit[INDIVIDUAL_COACHING_TOOLKIT]

  const { isToolkitAvailable: isParentCoachingToolkitAvailable, reasons: reasonsForParentCoachingAvailabilityStatus } =
    availabilityByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]

  const isFamilyHubAvailable = availabilityByToolkit[WELLNESS_TOOLKIT_PROMOTION.FAMILY].isToolkitAvailable

  const shouldShowForkedTriage = useShouldShowForkedTriageEntrypoint({
    isUserTeen,
    customerPropertiesMap,
    coachingToolkitAvailability: {
      individual: {
        available: isIndividualCoachingAvailable,
        reasons: reasonsForIndividualCoachingAvailabilityStatus,
      },
      parent: {
        available: isParentCoachingToolkitAvailable,
        reasons: reasonsForParentCoachingAvailabilityStatus,
      },
    },
    isLyraSelect,
  })

  useEffect(() => {
    async function fetchData() {
      const userAge = getAge({ date: userDOB ?? '' })
      const { age } = getMetadataAge({ dob: userDOB, matchFor: SEARCHER_TYPE_TO_MATCH_FOR.SELF, age: userAge })
      try {
        const results = await getTriageMetadata({ age, version: triageScreenersMetadataVersion, hideError: [404] })
        setPrimaryNeedsMetadata(
          ('payload' in results ? results.payload : results).find(
            (screener: TriageScreener) => screener.name === 'primaryNeeds',
          ),
        )
      } catch (e) {
        console.error(e)
        const error = e as Error | string
        logToSumo && logToSumo({ message: error instanceof Error ? error.message : error, type: 'ERROR' })
      } finally {
        setIsLoadingTriageMetadata(false)
      }
    }
    if (shouldFetchTriageMetadata && !primaryNeedsMetadata) {
      fetchData()
    }
  }, [
    userDOB,
    shouldFetchTriageMetadata,
    getMetadataAge,
    getTriageMetadata,
    triageScreenersMetadataVersion,
    primaryNeedsMetadata,
    logToSumo,
  ])

  // Send multiple primary needs experiment data for homebase when flag is enabled
  useSendMultiplePrimaryNeedsExperimentDataForHomebase({
    canSelectMultiplePrimaryNeeds,
    userId: lyraId,
    searchId,
    deviceUUID: deviceUUID || '',
    customerName,
    postExperimentEntrypoint: postExperimentEntrypoint || (() => {}),
    ldClient,
    isLyraSelect,
    isHeaderExperimentEnabled,
    route: route || '',
  })

  useEffect(() => {
    if (!isAiInTriageEnabledThreeArmInExperiment || !postExperimentEntrypoint || !deviceUUID || !lyraId) {
      return
    }
    const experimentPayload = {
      experimentUUID: ExperimentUUIDs.AiInTriageUUID,
      deviceUUID,
      experiments: {
        [ExperimentUUIDs.AiInTriageUUID]: {
          assignment: [
            {
              arm: isAiInTriageEnabledThreeArm,
              joinKeys: {
                client_lyra_id: lyraId,
              },
            },
          ],
        },
      },
    }
    postExperimentEntrypoint(experimentPayload)
  }, [
    deviceUUID,
    isAiInTriageEnabledThreeArm,
    isAiInTriageEnabledThreeArmInExperiment,
    lyraId,
    postExperimentEntrypoint,
  ])

  return {
    isFamilyHubAvailable,
    isParentToolkitAvailable: isParentCoachingToolkitAvailable,
    isIndividualCoachingToolkitsAvailable: isIndividualCoachingAvailable,
    shouldShowForkedTriage,
    isLoadingTriageMetadata,
    primaryNeedsMetadata,
    isSpeculativeSearchLoading: isLoading,
  }
}
